import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
    ClockIcon, 
    ListBulletIcon, 
    AcademicCapIcon, 
    BookOpenIcon, 
    DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { getLearningStatistics } from '../../services/questionAPIService';
import StandardLayout from '../../components/layout/StandardLayout';
import LearningStatus from './LearningStatus';
import TrialVolumeProgress from '../../components/common/TrialVolumeProgress';
import ActivityHeatmap from '../../components/activity/ActivityHeatmap';
import { AuthContext } from '../../context/AuthContext';

const HomePage = () => {
    const { hasSubscription } = useContext(AuthContext);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStatistics = async () => {
            try {
                const data = await getLearningStatistics();
                setStatistics(data);
                setLoading(false);
            } catch (err) {
                setError('Failed to load learning statistics');
                setLoading(false);
            }
        };

        fetchStatistics();
    }, []);

    if (loading) {
        return (
            <StandardLayout>
                <div className="min-h-screen flex items-center justify-center">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    if (error) {
        return (
            <StandardLayout>
                <div className="min-h-screen flex items-center justify-center p-4">
                    <div className="alert alert-error max-w-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>Entschuldigung, etwas ist schiefgelaufen. Bitte versuche es später erneut.</span>
                    </div>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div>
                <div className="container mx-auto px-4 py-6 max-w-5xl">
                    {/* Welcome Section */}
                    <div className="text-center mb-6">
                        <BookOpenIcon className="w-16 h-16 mx-auto text-primary mb-4" />
                        <h1 className="text-2xl sm:text-3xl font-bold mb-2">
                            Willkommen an Bord
                        </h1>
                        <p className="text-base-content/70">
                            Wähle deinen Lernmodus und starte durch
                        </p>
                        <Link 
                            to="/wiki/erfolgreich-lernen"
                            className="link link-primary text-sm inline-flex items-center gap-1 mt-2 hover:gap-2 transition-all duration-200"
                        >
                            <span>Wie lerne ich am besten?</span>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                        </Link>
                    </div>

                    {/* Learning Progress and Activity Section */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
                        {/* Learning Progress */}
                        <div className="md:col-span-7">
                            <div className="card bg-base-200 shadow-lg h-full">
                                <div className="card-body p-4">
                                    <LearningStatus 
                                        statistics={statistics}
                                        loading={loading}
                                        error={error}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Activity Heatmap */}
                        <div className="md:col-span-5">
                            <ActivityHeatmap activityData={statistics?.activity} />
                        </div>
                    </div>

                    {/* Learning Modes */}
                    <div className="space-y-4 mb-8">
                        <h2 className="text-lg font-bold">Lernmodi</h2>
                        
                        {/* Grid für die Lernmodi-Karten */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Quick Learn */}
                            <Link 
                                to="/micro-learning"
                                className="card bg-base-200 shadow-lg hover:shadow-md transition-all duration-300"
                            >
                                <div className="card-body p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="rounded-lg bg-primary/10 p-3">
                                            <ClockIcon className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-lg">Schnelles Lernen</h3>
                                            <p className="text-sm text-base-content/70 truncate">10 Minuten Lerneinheit mit zufälligen Fragen</p>
                                        </div>
                                        <div className="badge badge-primary shrink-0">10 Min</div>
                                    </div>
                                </div>
                            </Link>

                            {/* Spaced Learning */}
                            {statistics?.due_for_repetition > 0 ? (
                                <Link 
                                    to="/spaced-learning"
                                    className="card bg-base-200 shadow-lg hover:shadow-md transition-all duration-300"
                                >
                                    <div className="card-body p-4">
                                        <div className="flex items-center gap-4">
                                            <div className="rounded-lg bg-primary/10 p-3">
                                                <AcademicCapIcon className="w-6 h-6 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-lg">Wiederholung</h3>
                                                    <div className="badge badge-primary badge-sm">
                                                        {statistics.due_for_repetition}
                                                    </div>
                                                </div>
                                                <p className="text-sm text-base-content/70 truncate">Wiederhole Fragen, die du üben solltest</p>
                                            </div>
                                            <div className="badge badge-primary shrink-0">5 Min</div>
                                        </div>
                                    </div>
                                </Link>
                            ) : (
                                <div className="card bg-base-200 shadow-lg opacity-50">
                                    <div className="card-body p-4">
                                        <div className="flex items-center gap-4">
                                            <div className="rounded-lg bg-base-200 p-3">
                                                <AcademicCapIcon className="w-6 h-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-lg">Wiederholung</h3>
                                                <p className="text-sm text-base-content/70 truncate">Aktuell keine Fragen zur Wiederholung</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Exam Mode */}
                            <Link 
                                to="/pruefungsboegen"
                                className="card bg-base-200 shadow-lg hover:shadow-md transition-all duration-300"
                            >
                                <div className="card-body p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="rounded-lg bg-primary/10 p-3">
                                            <DocumentTextIcon className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-lg">Prüfungsbögen</h3>
                                            <p className="text-sm text-base-content/70 truncate">Übe mit Prüfungsfragen</p>
                                        </div>
                                        <div className="badge badge-primary shrink-0">15 Min</div>
                                    </div>
                                </div>
                            </Link>

                            {/* Wiki */}
                            <Link 
                                to="/wiki"
                                className="card bg-base-200 shadow-lg hover:shadow-md transition-all duration-300"
                            >
                                <div className="card-body p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="rounded-lg bg-primary/10 p-3">
                                            <BookOpenIcon className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-lg">Wiki</h3>
                                            <p className="text-sm text-base-content/70 truncate">Erfahre mehr über die SKS-Prüfung</p>
                                        </div>
                                    </div>
                                </div>
                            </Link>

                            {/* Question Catalog */}
                            <Link 
                                to="/alle-fragen"
                                className="card bg-base-200 shadow-lg hover:shadow-md transition-all duration-300"
                            >
                                <div className="card-body p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="rounded-lg bg-primary/10 p-3">
                                            <ListBulletIcon className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-lg">Fragenkatalog</h3>
                                            <p className="text-sm text-base-content/70 truncate">Durchsuche alle verfügbaren Fragen</p>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Trial Volume Progress if the user does not have a subscription */}
                    {!hasSubscription && <TrialVolumeProgress />}
                </div>
            </div>
        </StandardLayout>
    );
};

export default HomePage;
